import React, { useEffect, useState, useRef, useMemo, useContext } from "react";
import { connect } from "react-redux";
import { Card } from "react-md";
import MSplitter from "components/Shared/MSplitter.tsx";
import MGrid, { ACTION_ADD, MGridColumnDefinition } from "components/Shared/MGrid.tsx";
import { getSearchParameter } from "components/Shared/Functions";
import { DATA_TYPE_BOOLEAN } from "typeConstants/dataTypes";
import { userHasRight } from "selectors/rights";
import { CUSTOMER_INSIGHT_NOTIFICATION_VIEW, CUSTOMER_INSIGHT_NOTIFICATION_EDIT } from "typeConstants/rights";
import { CustomerInsightNotificationResponse, CustomerInsightNotificationAddRequest } from "interfaces/Mueller.Business.Models.ts";
import { RouteProps } from "react-router";
import { DialogContext } from "components/Shared/DialogProvider.tsx";
import { searchCustomerInsightNotifications, addCustomerInsightNotification } from "requests/customerInsights";
import responseHandler from "responseHandler/index.js";
import AddCustomerInsightNotification from "./AddCustomerInsightNotification.tsx";

interface CustomerInsightNotificationListProps {
  splitComponent: React.ComponentType<any>;  
  splitComponentTitle: string;
  // Router
  location: RouteProps["location"];
  // Redux
  canViewCustomerDetails: boolean;
  canEditCustomerDetails: boolean;
  errorToast: (response: Response) => void,
}

const CustomerInsightNotificationList = (props: CustomerInsightNotificationListProps) => {
  const [selectedCustomerInsightNotification, setSelectedCustomerInsightNotification] = useState<number>();
  const [customerInsightNotifications, setCustomerInsightNotifications] = useState<CustomerInsightNotificationResponse[]>([]);
  const [fetchingCustomerInsightNotifications, setFetchingCustomerInsightNotifications] = useState<boolean>(false);
  const dialog = useContext(DialogContext);
  const mounted = useRef(true);

  useEffect(() => {      
    loadCustomerInsightNotifications();
    return () => {
      mounted.current = false;
    };
  }, []);

  const loadCustomerInsightNotifications = () => {
    setFetchingCustomerInsightNotifications(true);
    searchCustomerInsightNotifications(getSearchParameter(props.location), (resp) => {
      setCustomerInsightNotifications(resp);
      setFetchingCustomerInsightNotifications(false);
    }, (resp) => {
      props.errorToast(resp);
      setFetchingCustomerInsightNotifications(false);
    }, mounted);    
  }

  const columns = useMemo(() => {
    const columns: MGridColumnDefinition[] = [
      { key: "userName", name: "Name", dataSource: "name" },
      { key: "userEmail", name: "Email", dataSource: "email" },
      { key: "companyName", name: "Company", dataSource: "majorCompany" },
      { key: "companyRollup", name: "Major Company Rollup", dataSource: "isMajorCompanyRollup", dataType: DATA_TYPE_BOOLEAN},
      { key: "enabled", name: "Enabled", dataSource: "isEnabled", dataType: DATA_TYPE_BOOLEAN},
    ];  
    return columns;
  }, [props.canEditCustomerDetails]);

  const onRowClick = index => {
    let selected = customerInsightNotifications[index].id;    
    setSelectedCustomerInsightNotification(selected);
  };
  

  const clearCustomerInsightNotification = () => setSelectedCustomerInsightNotification(undefined);

  const onSubmit = (request: CustomerInsightNotificationAddRequest) => {
    addCustomerInsightNotification(
      request,
      () => { 
        dialog.hide(); 
        setFetchingCustomerInsightNotifications(true);
        loadCustomerInsightNotifications();
      },
      () => dialog.hide(),
      mounted
    );
  };

  const onAdd = () => {
    const dialogProps = { className: "add-customer-insight-notification-dialog" };
    dialog.show(
      "Add Customer Insight Notification",
      <AddCustomerInsightNotification onCancel={() => dialog.hide()} onSubmit={onSubmit} />,
      null,
      dialogProps);
  };

  let actions = [];
  if (props.canEditCustomerDetails) {
    actions.push({ code: ACTION_ADD, onClick: onAdd });
  }

  return (
    <MSplitter
      firstChild={
        <div className="padding-page-content full-height">
          <Card className="full-height">
            <MGrid
              headerText="Customer Insights"
              columnDefinitions={columns}
              data={customerInsightNotifications}
              isFetchingData={fetchingCustomerInsightNotifications}
              onRowClick={props.canEditCustomerDetails ? onRowClick : null}
              displayNumOfItemsFound={true}
              actions={actions}
            />
          </Card>
        </div>}
        secondChild={
          selectedCustomerInsightNotification != null &&
          props.splitComponent && (
            <props.splitComponent
              selectedNotification={selectedCustomerInsightNotification}
              refreshParentList={loadCustomerInsightNotifications}
              verticalDisplay
            />
          )
        }
      secondChildTitle={{
        title: props.splitComponentTitle,
        buttons: [
          { icon: "close", onClick: clearCustomerInsightNotification }
        ]
      }}
    />
  );
};

const mapDispatchToProps = (dispatch) => ({
  errorToast(response: Response) {
    responseHandler.error(response, dispatch, false);
  },
});

const mapStateToProps = state => ({
  canViewCustomerDetails: userHasRight(CUSTOMER_INSIGHT_NOTIFICATION_VIEW)(state),
  canEditCustomerDetails: userHasRight(CUSTOMER_INSIGHT_NOTIFICATION_EDIT)(state),
});

export default connect(mapStateToProps, mapDispatchToProps)(CustomerInsightNotificationList);
